import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import Pagination from "../Pagination";
import { Icon } from "@iconify/react";
import { useParams, useNavigate } from "react-router-dom";
import "./../../../assets/css/purchase/datatable.css";
import { getReport } from "../../../utils/ReportUtils";
import QuoteContractsModal from "../Modal/Quote/QuoteContractsModal";

export default function QuoteTable({
  quotes = [],
  onEdit,
  onDelete,
  fillAvailable = false,
  allowCreate = true,
  allowEdit = true,
  allowDelete = true,
  isAdmin = false,
  onStatusChange,
  // API search mode: ถ้าส่ง search + onSearchChange มา จะ call API แทน filter local
  search: searchProp,
  onSearchChange,
  focusedDocId = null,
  onFocusQuote,
  stateStorageKey = null,
}) {
  const [currentPage, setCurrentPage] = useState(() => {
    if (!stateStorageKey) return 1;
    try {
      const raw = localStorage.getItem(stateStorageKey);
      if (!raw) return 1;
      const parsed = JSON.parse(raw);
      const saved = Number(parsed?.currentPage);
      return Number.isFinite(saved) && saved > 0 ? saved : 1;
    } catch {
      return 1;
    }
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (!stateStorageKey) return 100;
    try {
      const raw = localStorage.getItem(stateStorageKey);
      if (!raw) return 100;
      const parsed = JSON.parse(raw);
      const saved = Number(parsed?.itemsPerPage);
      return Number.isFinite(saved) && saved > 0 ? saved : 100;
    } catch {
      return 100;
    }
  });
  const [searchLocal, setSearchLocal] = useState("");

  const isApiSearch = typeof onSearchChange === "function";
  // inputValue = ค่าที่พิมพ์ในช่อง (ทั้ง 2 mode)
  const [inputValue, setInputValue] = useState(isApiSearch ? searchProp ?? "" : "");
  const search = isApiSearch ? searchProp ?? "" : searchLocal;

  // sync inputValue เมื่อ searchProp เปลี่ยนจากภายนอก (เช่น clear)
  useEffect(() => {
    if (isApiSearch) setInputValue(searchProp ?? "");
  }, [searchProp, isApiSearch]);

  const triggerSearch = (v) => {
    if (isApiSearch) {
      onSearchChange(v);
      setCurrentPage(1);
    } else {
      setSearchLocal(v);
      setCurrentPage(1);
    }
  };
  const navigate = useNavigate();
  const { id } = useParams();
  const AR_Id = isAdmin ? null : id;

  const actionRef = useRef(null);
  const statusRef = useRef(null);
  const totalRef = useRef(null);
  const serviceRef = useRef(null);

  const [rightStatus, setRightStatus] = useState(0);
  const [rightTotal, setRightTotal] = useState(0);
  const [rightService, setRightService] = useState(0);

  const [hasScrollX, setHasScrollX] = useState(false);
  const [contractsModalQuote, setContractsModalQuote] = useState(null);

  const normalize = (v) => (v || "").trim();

  const groupQuotes = (rows) => {
    // ถ้า backend ส่ง CT_List มาแล้ว (group แล้ว) ก็ไม่ต้อง group อีก
    const hasCTList = rows.length > 0 && Array.isArray(rows[0]?.CT_List);
    if (hasCTList) {
      return rows; // Backend group แล้ว ไม่ต้อง group อีก
    }
    
    // ถ้ายังไม่มี CT_List ให้ group เอง (backward compatibility)
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.Doc_Id)) {
        map.set(r.Doc_Id, { ...r, CT_List: [] });
      }
      if (r.CT_Id) {
        map.get(r.Doc_Id).CT_List.push({
          CT_Id: r.CT_Id,
          CT_No: r.CT_No,
          CT_Status: r.CT_Status,
        });
      }
    });
    return Array.from(map.values());
  };

  const handleDelete = (quote) => {
    Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: `ต้องการลบใบเสนอราคา ${quote.Doc_No}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (result.isConfirmed) onDelete?.(quote);
    });
  };

  const groupedData = groupQuotes(quotes);

  // ถ้าเป็น API search mode → ไม่ filter local (backend กรองมาแล้ว)
  const filteredQuotes = isApiSearch
    ? groupedData
    : groupedData.filter((q) => {
        const keyword = search.toLowerCase();
        return (
          normalize(q.Doc_No).toLowerCase().includes(keyword) ||
          normalize(q.Service_Name).toLowerCase().includes(keyword) ||
          normalize(q.AR_Name).toLowerCase().includes(keyword) ||
          normalize(q.CustomerName).toLowerCase().includes(keyword)
        );
      });

  const totalItems = filteredQuotes.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredQuotes.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  useEffect(() => {
    if (!stateStorageKey) return;
    try {
      const raw = localStorage.getItem(stateStorageKey);
      const prev = raw ? JSON.parse(raw) : {};
      localStorage.setItem(
        stateStorageKey,
        JSON.stringify({
          ...prev,
          currentPage,
          itemsPerPage,
        })
      );
    } catch {}
  }, [currentPage, itemsPerPage, stateStorageKey]);

  const handleCreate = () => {
    if (isAdmin) navigate("/admin/quote/create");
    else {
      if (!AR_Id)
        return Swal.fire("ผิดพลาด!", "ไม่พบรหัสลูกค้า (AR_Id)", "error");
      navigate(`/quote/create/${AR_Id}`);
    }
  };

  const getCustomerDisplayName = (q) => {
    const name = normalize(q.AR_Name);
    const last = normalize(q.AR_LastName);
    const display = normalize(q.CustomerName);
    if (name && last) return `${name} ${last}`;
    if (!last && display) return display;
    return name || display || "-";
  };

  const renderStatus = (quote) => {
    const status = String(quote.Doc_Status);
    const hasActiveContract =
      quote.CT_List && quote.CT_List.some((c) => Number(c.CT_Status) !== 5);
    if (status === "3") {
      return (
        <span className="bg-red-100 text-red-700 px-2 py-1 rounded-md text-xs">
          ไม่อนุมัติ
        </span>
      );
    }
    if (hasActiveContract) {
      if (status === "2")
        return (
          <span className="bg-green-100 text-green-700 px-2 py-1 rounded-md text-xs">
            อนุมัติ
          </span>
        );
      if (status === "1")
        return (
          <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md text-xs">
            รออนุมัติ
          </span>
        );
      return (
        <span className="bg-red-100 text-red-700 px-2 py-1 rounded-md text-xs">
          ไม่อนุมัติ
        </span>
      );
    }
    if (isAdmin && allowEdit) {
      const bgColor =
        status === "2"
          ? "bg-green-100 text-green-700 border-green-400"
          : status === "1"
          ? "bg-yellow-100 text-yellow-700 border-yellow-400"
          : "bg-red-100 text-red-700 border-red-400";
      return (
        <select
          value={status}
          onChange={(e) => onStatusChange?.(quote, Number(e.target.value))}
          className={`border rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-blue-400 ${bgColor}`}
        >
          <option value="1">รออนุมัติ</option>
          <option value="2">อนุมัติ</option>
          <option value="3">ไม่อนุมัติ</option>
        </select>
      );
    }
    if (status === "2")
      return (
        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-md text-xs">
          อนุมัติ
        </span>
      );
    if (status === "1")
      return (
        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-md text-xs">
          รออนุมัติ
        </span>
      );
    return (
      <span className="bg-red-100 text-red-700 px-2 py-1 rounded-md text-xs">
        ไม่อนุมัติ
      </span>
    );
  };

  const formatNumber = (num) => {
    if (num == null || isNaN(num)) return "-";
    return Number(num).toLocaleString("th-TH", { minimumFractionDigits: 2 });
  };

  const getContractColor = (status) => {
    switch (Number(status)) {
      case 0:
        return "#9CA3AF";
      case 1:
        return "#FACC15";
      case 2:
        return "#22C55E";
      case 4:
        return "#3B82F6";
      case 5:
        return "#EF4444";
      default:
        return "#6B7280";
    }
  };

  useEffect(() => {
    let A = 0,
      S = 0,
      T = 0,
      B = 0;

    if (actionRef.current) A = actionRef.current.getBoundingClientRect().width;
    if (statusRef.current) S = statusRef.current.getBoundingClientRect().width;
    if (totalRef.current) T = totalRef.current.getBoundingClientRect().width;
    if (serviceRef.current) B = serviceRef.current.getBoundingClientRect().width;

    setRightStatus(A);
    setRightTotal(A + S);
    setRightService(A + S + T);
  }, []);

  // ตรวจว่า table มี scrollbar X ไหม
  useEffect(() => {
    const wrapper = document.querySelector(".table-wrapper-scroll");
    if (wrapper) {
      setHasScrollX(wrapper.scrollWidth > wrapper.clientWidth);
    }
  }, [filteredQuotes]);

  useEffect(() => {
    if (!focusedDocId) return;
    const activeRow = document.querySelector(
      ".table-wrapper-scroll tbody tr.active-focus-row"
    );
    if (!activeRow) return;
    requestAnimationFrame(() => {
      activeRow.scrollIntoView({ block: "center", behavior: "auto" });
    });
  }, [focusedDocId, paginatedData.length]);

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm"
      style={{
        display: "flex",
        flexDirection: "column",
        height: fillAvailable ? "100%" : "auto",
        maxHeight: fillAvailable ? "100%" : "calc(91vh - 320px)",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }

        .table-wrapper-scroll { padding: 0 0 0 20px !important; }

        table.table { border-collapse: collapse; width: 100%; min-width: 900px; }

        th, td {
          text-align: center !important;
          vertical-align: middle !important;
          border: 1px solid #ccc !important;
          padding: 8px 12px !important;
          white-space: nowrap;
        }

        thead th {
          background-color: #E13F57 !important;
          color: white !important;
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 40 !important;
        }

        /* sticky columns */
        th.sticky-action, td.sticky-action {
          position: sticky;
          right: 0px;
          background: white !important;
          z-index: 20;
          isolation: isolate;
        }

        th.sticky-status, td.sticky-status {
          position: sticky;
          right: ${hasScrollX ? rightStatus : 0}px;
          background: white !important;
          z-index: 99;
          isolation: isolate;
        }

        th.sticky-total, td.sticky-total {
          position: sticky;
          right: ${hasScrollX ? rightTotal : 0}px;
          background: white !important;
          z-index: 98;
          isolation: isolate;
        }

        th.sticky-service, td.sticky-service {
          position: sticky;
          right: ${hasScrollX ? rightService : 0}px;
          background: white !important;
          z-index: 97;
          isolation: isolate;
        }

        thead th.sticky-action {
          background-color: #E13F57 !important;
          z-index: 21 !important;
        }

        thead th.sticky-status {
          background-color: #E13F57 !important;
          z-index: 101 !important;
        }

        thead th.sticky-total {
          background-color: #E13F57 !important;
          z-index: 101 !important;
        }

        thead th.sticky-service {
          background-color: #E13F57 !important;
          z-index: 101 !important;
        }

        /* กันซ้อน */
        th.sticky-action, td.sticky-action,
        th.sticky-status, td.sticky-status,
        th.sticky-total, td.sticky-total,
        th.sticky-service, td.sticky-service {
          box-shadow: -2px 0 3px rgba(0,0,0,0.05);
        }

        tbody tr.active-focus-row td {
          background: #fce8eb !important;
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <h5 className="m-0 fw-bold">ใบเสนอราคา</h5>

          <div className="relative">
            <input
              type="text"
              placeholder="ค้นหา... (กด Enter)"
              value={isApiSearch ? inputValue : search}
              onChange={(e) => {
                if (isApiSearch) {
                  setInputValue(e.target.value);
                } else {
                  triggerSearch(e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (isApiSearch && e.key === "Enter") {
                  triggerSearch(inputValue);
                }
              }}
              className="h-[36px] w-[250px] border border-gray-300 rounded-md px-3 pr-8 py-1 text-sm focus:ring focus:ring-blue-200 placeholder:text-left"
            />

            {(isApiSearch ? inputValue : search) !== "" && (
              <button
                onClick={() => {
                  if (isApiSearch) {
                    setInputValue("");
                    onSearchChange?.("");
                  } else {
                    triggerSearch("");
                  }
                  setCurrentPage(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon icon="mdi:close" className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {allowCreate && (
          <button
            onClick={handleCreate}
            className="flex items-center gap-1 text-[#F17F25] text-sm font-semibold border-b-2 border-[#F17F25] pb-0.5 hover:text-[#c7641c] transition"
          >
            <Icon icon="mdi:plus-circle-outline" className="w-5 h-5" />
            เพิ่มใบเสนอราคา
          </button>
        )}
      </div>

      <div
        className="table-wrapper-scroll flex-1"
        style={{ overflowY: "auto", overflowX: "auto", flexGrow: 1 }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead>
            <tr>
              <th style={{ width: "12%" }}>วันที่เสนอราคา</th>
              <th style={{ width: "15%" }}>เลขที่เอกสาร</th>
              <th style={{ width: "12%" }}>ประเภทลูกค้า</th>
              <th style={{ width: "20%" }}>ชื่อที่ระบุในสัญญา</th>
              <th style={{ width: "20%" }}>ชื่อลูกค้า</th>

              <th
                ref={serviceRef}
                className="sticky-service"
                style={{ width: "25%" }}
              >
                บริการ
              </th>

              <th
                ref={totalRef}
                className="sticky-total"
                style={{ width: "12%" }}
              >
                ยอดรวม (บาท)
              </th>

              <th
                ref={statusRef}
                className="sticky-status"
                style={{ width: "12%" }}
              >
                สถานะ
              </th>

              <th
                ref={actionRef}
                className="sticky-action"
                style={{ width: "380px" }}
              >
                การจัดการ
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((q, i) => {
                const contractCount = q.CT_List?.length ?? 0;
                const canOpenContracts = Number(q.Doc_Status) !== 3;

                return (
                  <tr
                    key={q.Doc_Id || i}
                    className={
                      String(q.Doc_Id ?? "") === String(focusedDocId || "")
                        ? "active-focus-row"
                        : ""
                    }
                  >
                    <td>
                      {q.Doc_Date
                        ? new Date(q.Doc_Date).toLocaleDateString("th-TH")
                        : "-"}
                    </td>

                    <td>{q.Doc_No || "-"}</td>

                    <td>
                      {q.AR_Group === 1
                        ? "บุคคลธรรมดา"
                        : q.AR_Group === 2
                        ? "นิติบุคคล"
                        : "-"}
                    </td>

                    <td className="text-start">
                      {q.AR_Id ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary text-decoration-none text-start border-0 hover:text-blue-700"
                          onClick={() => navigate(`/EditCustomer/${q.AR_Id}`)}
                          style={{ cursor: "pointer" }}
                        >
                          {q.DisplayName || "-"}
                        </button>
                      ) : (
                        q.DisplayName || "-"
                      )}
                    </td>

                    <td className="text-start">
                      {normalize(q.AR_Name)} {normalize(q.AR_LastName)}
                    </td>

                    <td className="text-start sticky-service">
                      {q.Service_Name || "-"}
                    </td>

                    <td className="text-end sticky-total">
                      {formatNumber(q.Total_Amount)}
                    </td>

                    <td className="sticky-status">{renderStatus(q)}</td>

                    <td className="sticky-action">
                      {Number(q.Doc_Status) === 3 ? null : (
                        <div
                          className="d-grid"
                          style={{
                            gridTemplateColumns:
                              "110px 65px 65px 65px",
                            gap: "6px",
                            justifyContent: "center",
                          }}
                        >
                          {canOpenContracts ? (
                            <button
                              onClick={() => setContractsModalQuote(q)}
                              className="btn btn-sm text-white d-flex align-items-center justify-content-center text-center"
                              style={{
                                backgroundColor: contractCount > 0 ? "#598AE4" : "#76D7C4",
                                width: "110px",
                              }}
                            >
                              {contractCount > 0
                                ? `สัญญา (${contractCount})`
                                : "ดูสัญญา"}
                            </button>
                          ) : (
                            <span
                              className="btn btn-sm d-flex align-items-center justify-content-center text-center"
                              style={{
                                width: "110px",
                                backgroundColor: "#e5e7eb",
                                color: "#999",
                                cursor: "not-allowed",
                              }}
                            >
                              รออนุมัติ
                            </span>
                          )}

                          {allowEdit && (
                            <button
                              onClick={() => {
                                onFocusQuote?.(q);
                                onEdit?.(q, q.AR_Id);
                              }}
                              className="btn btn-sm text-white d-flex align-items-center justify-content-center"
                              style={{
                                backgroundColor: "#598AE4",
                                width: "65px",
                              }}
                            >
                              แก้ไข
                            </button>
                          )}

                          {allowDelete && (
                            <button
                              onClick={() => handleDelete(q)}
                              className="btn btn-sm text-white d-flex align-items-center justify-content-center"
                              style={{
                                backgroundColor: "#E13F57",
                                width: "65px",
                              }}
                            >
                              ลบ
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => {
                              if (!q.Doc_Id)
                                return Swal.fire(
                                  "ไม่พบรหัสเอกสาร",
                                  "ไม่สามารถพิมพ์รายงานได้",
                                  "warning"
                                );
                              getReport("qt", q.Doc_Id);
                            }}
                            className="btn btn-sm text-white d-flex justify-content-center"
                            style={{ width: "65px" }}
                          >
                            <Icon icon="mdi:printer" width="16" color="#007bff" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={9} className="text-center py-4 text-muted">
                  ไม่มีข้อมูลใบเสนอราคา
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ flexShrink: 0 }}>
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(val) => {
            setItemsPerPage(val);
            setCurrentPage(1);
          }}
        />
      </div>

      <QuoteContractsModal
        isOpen={!!contractsModalQuote}
        onClose={() => setContractsModalQuote(null)}
        quote={contractsModalQuote}
      />
    </div>
  );
}
